import { useQuery } from '@tanstack/vue-query';
import { useUserStore } from '@store/user';
import type { Ref } from 'vue';
import type { TaskService } from './useTask';

export interface Transport {
    name: string;
    registration: string;
}

export interface VehicleInfo {
    make: string;
    model: string;
    registration: string;
    registration_state: string;
    reference_number: string;
}

export interface PetInfo {
    name: string;
    animal: string;
    breed: string;
    age: number;
    weight: number;
}

export interface TaskAddress {
    type: string;
    address: string;
    lat: number;
    lng: number;
}

export interface Task {
    id: number;
    schedule_id: number;
    schedule_service_id: number;
    order_id: number;
    order_item_id?: number | null;
    order_item_status?: string;
    action: string;
    action_label: string;
    title: string;
    addresses: TaskAddress[];
    time_start: string;
    time_end: string;
    start_timezone?: string;
    start_timezone_code?: string;
    end_timezone?: string;
    end_timezone_code?: string;
    date?: string;
    status: string;
    route?: {
        from: string;
        to: string;
    };
    customer: string;
    customer_phone: string;
    order_reference: string;
    sequence: number;
    started_at?: string;
    total_break_seconds?: number | null;
    transports?: Transport[];
    vehicles?: VehicleInfo[];
    pets?: PetInfo[];
    service?: TaskService | null;
}

export interface CalendarDate {
    date: string;
    count: number;
}

export interface TaskStats {
    jobs: number;
    tasks: number;
    stops: number;
}

const EMPTY_STATS: TaskStats = { jobs: 0, tasks: 0, stops: 0 };

export function useTodaysTasks() {
    const userStore = useUserStore();

    const { data, isLoading, isFetching, refetch, error } = useQuery({
        queryKey: ['me/tasks', null, userStore.tenantId],
        queryFn: async (): Promise<{ tasks: Task[]; stats: TaskStats }> => {
            const resp = await window.tenantAxios.get('/me/tasks');
            return {
                tasks: resp.data.tasks || [],
                stats: resp.data.stats ?? EMPTY_STATS,
            };
        },
        retry: false,
        placeholderData: { tasks: [], stats: EMPTY_STATS },
        staleTime: 5000,
    });

    const tasks = computed(() => data.value?.tasks ?? []);
    const stats = computed(() => data.value?.stats ?? EMPTY_STATS);

    return {
        tasks,
        stats,
        isLoading,
        isFetching,
        refetch,
        error,
    };
}

export function useMyPendingTasks() {
    const userStore = useUserStore();

    const {
        data: tasks,
        isLoading,
        isFetching,
        refetch,
        error,
    } = useQuery({
        queryKey: ['me/tasks/pending', userStore.tenantId],
        queryFn: async (): Promise<Task[]> => {
            const resp = await window.tenantAxios.get('/me/tasks/pending');
            return resp.data.tasks || [];
        },
        retry: false,
        placeholderData: [],
        staleTime: 5000,
    });

    return {
        tasks,
        isLoading,
        isFetching,
        refetch,
        error,
    };
}

/**
 * Tasks the office accepted on the current user's behalf, listed in the
 * Accepted section of the notifications page with a Dismiss action. Shares
 * its query key with the diary store's invalidateMyTasks so a dismiss (or any
 * diary refresh) refreshes this list.
 */
export function useMyAcceptedOnBehalfTasks() {
    const userStore = useUserStore();

    const {
        data: tasks,
        isLoading,
        isFetching,
        refetch,
        error,
    } = useQuery({
        queryKey: ['me/tasks/accepted-on-behalf', userStore.tenantId],
        queryFn: async (): Promise<Task[]> => {
            const resp = await window.tenantAxios.get('/me/tasks/accepted-on-behalf');
            return resp.data.tasks || [];
        },
        retry: false,
        placeholderData: [],
        staleTime: 5000,
    });

    return {
        tasks,
        isLoading,
        isFetching,
        refetch,
        error,
    };
}

/**
 * Count of tasks awaiting the current user's acceptance, for the nav badge.
 * Shares the query key with useMyPendingTasks so the badge and the
 * notifications page dedupe to a single request, and accept/decline
 * invalidation (diary store invalidateDiaries) refreshes both. Only enabled
 * for crew (FIELD_STAFF/CONTRACTOR) — `false` stops OWNER/ADMIN from
 * auto-qualifying via the hasAnyRole admin shortcut. Polls every 60s since
 * there is no broadcast event for new staff assignments.
 */
export function usePendingTasksCount() {
    const userStore = useUserStore();

    const { data: tasks } = useQuery({
        queryKey: ['me/tasks/pending', userStore.tenantId],
        queryFn: async (): Promise<Task[]> => {
            const resp = await window.tenantAxios.get('/me/tasks/pending');
            return resp.data.tasks || [];
        },
        retry: false,
        staleTime: 5000,
        refetchInterval: 60_000,
        enabled: computed(() => userStore.hasAnyRole(['FIELD_STAFF', 'CONTRACTOR'], false)),
    });

    return {
        count: computed(() => tasks.value?.length ?? 0),
    };
}

export function useTasksByDate(date: Ref<string>) {
    const userStore = useUserStore();

    const {
        data: tasks,
        isLoading,
        isFetching,
        refetch,
        error,
    } = useQuery({
        queryKey: computed(() => ['me/tasks', date.value, userStore.tenantId]),
        queryFn: async (): Promise<Task[]> => {
            const resp = await window.tenantAxios.get('/me/tasks', {
                params: { date: date.value },
            });
            return resp.data.tasks || [];
        },
        retry: false,
        placeholderData: [],
        staleTime: 5000,
    });

    return {
        tasks,
        isLoading,
        isFetching,
        refetch,
        error,
    };
}

export function useTaskCalendar(start: Ref<string>, end: Ref<string>) {
    const userStore = useUserStore();

    const {
        data: dates,
        isLoading,
        isFetching,
        refetch,
        error,
    } = useQuery({
        queryKey: computed(() => ['me/tasks/calendar', start.value, end.value, userStore.tenantId]),
        queryFn: async (): Promise<CalendarDate[]> => {
            const resp = await window.tenantAxios.get('/me/tasks/calendar', {
                params: { start: start.value, end: end.value },
            });
            return resp.data.dates || [];
        },
        retry: false,
        placeholderData: [],
        staleTime: 30000,
        enabled: computed(() => !!start.value && !!end.value),
    });

    return {
        dates,
        isLoading,
        isFetching,
        refetch,
        error,
    };
}
